import { Briefcase } from 'lucide-react';
import { getTenantColor } from '../../lib/colors';
import { cn } from '../../lib/utils';

export type TenantLogoTenant = {
  code?: string;
  name?: string;
  color?: string | null;
  logoUrl?: string | null;
};

const SIZE_CLASS = {
  xs: 'h-5 w-5',
  sm: 'h-7 w-7',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
  xl: 'h-16 w-16',
} as const;

type TenantLogoSize = keyof typeof SIZE_CLASS;

function logoSrc(logoUrl: string): string {
  if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) return logoUrl;
  return logoUrl.startsWith('/') ? logoUrl : `/${logoUrl}`;
}

export function TenantLogo({
  tenant,
  size = 'md',
  className,
  rounded = 'full',
}: {
  tenant: TenantLogoTenant;
  size?: TenantLogoSize;
  className?: string;
  rounded?: 'full' | 'lg' | 'md';
}) {
  const roundedClass = rounded === 'full' ? 'rounded-full' : rounded === 'lg' ? 'rounded-lg' : 'rounded-md';
  const color = getTenantColor(tenant);
  const label = tenant.name || tenant.code || 'Tenant';

  if (tenant.logoUrl) {
    return (
      <img
        src={logoSrc(tenant.logoUrl)}
        alt=""
        aria-label={label}
        className={cn(SIZE_CLASS[size], roundedClass, 'object-cover shrink-0 bg-white dark:bg-gray-900', className)}
      />
    );
  }

  const iconSize =
    size === 'xs' ? 'h-3 w-3' : size === 'sm' ? 'h-4 w-4' : size === 'md' ? 'h-5 w-5' : size === 'lg' ? 'h-6 w-6' : 'h-7 w-7';

  return (
    <span
      className={cn(
        SIZE_CLASS[size],
        roundedClass,
        'inline-flex shrink-0 items-center justify-center bg-gray-100 dark:bg-gray-800',
        className,
      )}
      style={{ color }}
      aria-hidden
    >
      <Briefcase className={iconSize} />
    </span>
  );
}

export function TenantHeading({
  tenant,
  title,
  subtitle,
  className,
}: {
  tenant: TenantLogoTenant;
  title: string;
  subtitle?: string;
  className?: string;
}) {
  return (
    <div className={cn('flex items-center gap-3 min-w-0', className)}>
      <TenantLogo tenant={tenant} size="md" />
      <div className="min-w-0">
        <h2 className="text-xl font-bold truncate" style={{ color: getTenantColor(tenant) }}>
          {title}
        </h2>
        {subtitle ? (
          <p className="text-xs text-gray-500 dark:text-gray-400 font-mono truncate">{subtitle}</p>
        ) : null}
      </div>
    </div>
  );
}
